import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Modal from '../../components/modal/modal';
import { subscribeVideoOverlayCloseRequest } from '../../utils/video-overlay/video-overlay-close-request';

import { getChannelForWidget } from '@wix/wix-vod-shared/common';
import { Loadable } from '@wix/wix-vod-shared/components';
import WindowSize from '../../containers/window-size';
import PlayerModuleLoader from '../../data-components/player-module-loader';
import { PersistentEvents } from '../../components/persistent-events/persistent-events';
import ReloadChannelPaymentDataOnPayment from '../../data-components/reload-channel-payment-data-on-payment';
import ClearVideoUrlsCacheOnPayment from '../../data-components/clear-video-urls-cache-on-payment';
import { getCurrentSiteUser } from '../../selectors/current-site-user';
import { getVideosGroupedByIds } from '../../selectors/videos';
import { canPlayFullOrPreview } from '@wix/wix-vod-shared/dist/esm/widget/ui-selectors/video-access';

import { selectVideo as selectVideoAction } from '../../redux/actions/select-video';
import {
  playVideo as playVideoAction,
  pauseVideo as pauseVideoAction,
} from '../../redux/actions/player/change-playback-status';
import { ensureVideoLoaded as ensureVideoLoadedAction } from '../../redux/actions/video/ensure-video-loaded';
import { getPaymentInfo } from '../../redux/actions/channel/payment';
import { getPublicVideo } from '../../redux/actions/channel/video/get';
import { setSavedTime as setSavedTimeAction } from '../../redux/actions/player/set-saved-time';

import {
  loadMoreVideoPages,
  prependVideoIds as prependVideoIdsAction,
} from '../../redux/lazy-channel-videos/actions';

import CLOSE_REASONS from '../constants/close-reasons';
import getQueryParams from '../../utils/get-query-params';
import {
  isMobile as isMobileSelector,
  isPortableDevice as isPortableDeviceSelector,
} from '../../selectors/form-factor';
import { getCompId } from '../../redux/hydrated-data/hydrated-data';
import { LoginWatcher } from '../../containers/login-watcher';

const mapStateToProps = (state, ownProps) => ({
  videoId: state.selectedVideoId || ownProps.match.params.videoId,
  appSettings: state.appSettings,
  isMobile: isMobileSelector(state),
  currentSiteUser: getCurrentSiteUser(state),
  channel: getChannelForWidget(state),
  videoByIds: getVideosGroupedByIds(state),
  compId: getCompId(state),
  isPortableDevice: isPortableDeviceSelector(state),
});

const mapDispatchToProps = {
  getPaymentInfo,
  getPublicVideo,
  selectVideo: selectVideoAction,
  playVideo: playVideoAction,
  pauseVideo: pauseVideoAction,
  setSavedTime: setSavedTimeAction,
  loadMoreVideoPages,
  prependVideoIds: prependVideoIdsAction,
  ensureVideoLoaded: ensureVideoLoadedAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  class PlayerLayout extends React.Component {
    static propTypes = {
      children: PropTypes.element.isRequired,
      videoId: PropTypes.string.isRequired,
      close: PropTypes.func.isRequired,
      match: PropTypes.object.isRequired,
      appSettings: PropTypes.object.isRequired,
      channel: PropTypes.object.isRequired,
      videoByIds: PropTypes.object.isRequired,
      location: PropTypes.object.isRequired,

      getPaymentInfo: PropTypes.func.isRequired,
      ensureVideoLoaded: PropTypes.func.isRequired,
      getPublicVideo: PropTypes.func.isRequired,
      selectVideo: PropTypes.func.isRequired,
      playVideo: PropTypes.func.isRequired,
      pauseVideo: PropTypes.func,
      setSavedTime: PropTypes.func,
      isChannelView: PropTypes.bool.isRequired,
      isMobile: PropTypes.bool.isRequired,
      isPortableDevice: PropTypes.bool.isRequired,
      currentSiteUser: PropTypes.object,
      loadMoreVideoPages: PropTypes.func.isRequired,
      prependVideoIds: PropTypes.func.isRequired,
    };

    componentDidMount() {
      this.unsubscribeVideoOverlayCloseRequest =
        subscribeVideoOverlayCloseRequest(this.props.compId, () => {
          this.props.close(CLOSE_REASONS.SUCCEED);
        });
    }

    componentWillUnmount() {
      this.unsubscribeVideoOverlayCloseRequest();
    }

    loadData = () => {
      const { channelId } = this.props.match.params;
      const { videoId } = this.props;

      this.props.loadMoreVideoPages();

      return Promise.all([
        this.props.getPaymentInfo(channelId),
        this.props.getPublicVideo(channelId, videoId),
      ]);
    };

    selectOrPlayVideo = () => {
      const {
        channel,
        currentSiteUser,
        playVideo,
        selectVideo,
        videoId,
        videoByIds,
        location,
        prependVideoIds,
        isChannelView,
        isPortableDevice,
      } = this.props;

      if (!isChannelView) {
        const isPlayAllowed = canPlayFullOrPreview(
          channel,
          videoByIds[videoId],
          currentSiteUser,
        );

        if (channel.featuredItem) {
          prependVideoIds(channel.featuredItem);
        }

        if (
          isPlayAllowed &&
          getQueryParams(location.search).autoplay === 'true'
        ) {
          selectVideo(videoId);
          playVideo(videoId, isPortableDevice);
          return;
        }

        selectVideo(videoId);
      }
    };

    processDataFromStorage = (data) => {
      const { selectedVideoId, openOverlay, autoplay, savedPlayerTime } = data;
      const { selectVideo, playVideo, setSavedTime, ensureVideoLoaded } =
        this.props;

      if (savedPlayerTime) {
        setSavedTime(savedPlayerTime);
      }

      // NOTE: overlay is already opened, so `openOverlay` event should be already handled by overlay opener
      if (openOverlay || !selectedVideoId) {
        return;
      }

      ensureVideoLoaded(selectedVideoId).then(() => {
        if (autoplay) {
          playVideo(selectedVideoId);
        } else {
          selectVideo(selectedVideoId);
        }
      });
    };

    render() {
      const { children, isMobile, ...props } = this.props;

      return (
        <WindowSize>
          <PlayerModuleLoader>
            <Loadable
              loader={this.loadData}
              onDataLoaded={this.selectOrPlayVideo}
            >
              <React.Fragment>
                <PersistentEvents onEvent={this.processDataFromStorage} />
                {children}
                <ReloadChannelPaymentDataOnPayment />
                <ClearVideoUrlsCacheOnPayment />
                <LoginWatcher />
                <Modal isMobile={isMobile} settings={props.appSettings} />
              </React.Fragment>
            </Loadable>
          </PlayerModuleLoader>
        </WindowSize>
      );
    }
  },
);
